import { Button } from 'primereact/button';

import { PermissionDeniedMessages } from '@/components/providers/PermissionsContextProvider/Permissions.types';
import { usePermissionGate } from '@/components/providers/PermissionsContextProvider/usePermissionGate';
import { ClampedText } from '@/components/shared/components/ClampedText';
import { FormattedText } from '@/components/shared/components/FormattedText';
import { Linkify } from '@/components/shared/components/Linkify';
import { useFeatureFlags } from '@/components/shared/constants/featureFlags';
import { Feature } from '@/components/shared/constants/features';

import {
    Checkbox,
    ContentFooter,
    DddPendingPill,
    DddPendingPillButton,
    DeletedPill,
    Description,
    DocumentsCountPill,
    FilledCheckbox,
    HeaderContent,
    HeaderRow,
    LinkedConditionPill,
    LinkedConditionPillButton,
    MetaRow,
    NoNotesText,
    NotesHint,
    NotesPreview,
    ProblematicPill,
    ProblematicPillButton,
    ProductPill,
    ResolvedPill,
    StrikethroughDescription,
    WarningPill,
} from './ApplicantConditions.StyledComponents';
import {
    Condition,
    ConditionResolutionMethod,
    ConditionResolutionStatus,
    ResolveConditionOptions,
} from './ApplicantConditions.types';
import {
    AUTO_RESOLVE_NOTE_TEMPLATE,
    formatDateShort,
    formatDateTime,
    getProductScopeLabel,
} from './ApplicantConditions.utils';
import { ConditionActionMenu } from './ConditionActionMenu';
import { ConditionNoteForm } from './ConditionNoteForm';
import { ConditionNotesTimeline } from './ConditionNotesTimeline';
import { getReviewTeams } from './conditionTemplates';
import { ReviewTeamPills } from './ReviewTeamPills';
import { WatcherAvatarStack } from './WatcherAvatarStack';

interface ContentProps {
    applicantId: string;
    condition: Condition;
    onDelete?: () => void;
    onResolve?: (options?: ResolveConditionOptions) => void;
    onAttachDocument?: () => void;
    onAddFollowUp?: () => void;
    highlightNoteId?: string | null;
}

interface HeaderProps {
    applicantId: string;
    condition: Condition;
    onCheckboxClick?: () => void;
    onDocumentsClick?: () => void;
    onDelete?: () => void;
    onEdit?: () => void;
    onAddFollowUp?: () => void;
    onOpen?: () => void;
    isOpen?: boolean;
    onNavigateToCondition?: (conditionId: string) => void;
    onNavigateToDdd?: () => void;
}

export const ConditionHeader = ({
    applicantId,
    condition,
    onCheckboxClick,
    onDocumentsClick,
    onDelete,
    onEdit,
    onAddFollowUp,
    onOpen,
    isOpen: cardIsOpen,
    onNavigateToCondition,
    onNavigateToDdd,
}: HeaderProps) => {
    const scope = getProductScopeLabel(condition.products);
    const noteCount = condition.notes.length;
    const fileCount = condition.files.length;

    const {
        isBlocksApprovalEnforcementEnabled,
        isConditionResolutionStatusEnabled,
        isConditionTextFormattingEnabled,
        isConditionsWatcherEnabled,
        isDddPendingPillEnabled,
    } = useFeatureFlags();
    const isOpen = !condition.resolved && !condition.isDeleted;
    const showDddPendingPill =
        isDddPendingPillEnabled && isOpen && condition.dddOutstandingCount != null;
    const DddPill = onNavigateToDdd ? DddPendingPillButton : DddPendingPill;
    const isProblematic =
        isConditionResolutionStatusEnabled &&
        condition.resolutionStatus === ConditionResolutionStatus.Problematic;
    const isApproved =
        isConditionResolutionStatusEnabled &&
        condition.resolutionStatus === ConditionResolutionStatus.Approved;
    const childCount = condition.childConditionIds?.length ?? 0;
    const firstChildId = condition.childConditionIds?.[0];
    const blocksApprovalScope =
        isBlocksApprovalEnforcementEnabled && isOpen
            ? getProductScopeLabel(condition.blocksApproval)
            : '';

    const resolveGate = usePermissionGate(
        Feature.ProtectedConditions,
        PermissionDeniedMessages.ResolveCondition,
        condition.canResolve,
    );

    const handleDocumentsClick = (e: React.MouseEvent) => {
        if (!onDocumentsClick) return;
        e.preventDefault();
        e.stopPropagation();
        onDocumentsClick();
    };

    const handleNavigateClick = (targetId: string) => (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        onNavigateToCondition?.(targetId);
    };

    const handleNavigateToDddClick = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        onNavigateToDdd?.();
    };

    const handleCheckboxClick = resolveGate.gateAction((e: React.MouseEvent) => {
        if (!onCheckboxClick) return;
        e.preventDefault();
        e.stopPropagation();
        onCheckboxClick();
    });

    const checkboxIsGated = Boolean(onCheckboxClick) && resolveGate.disabled;

    return (
        <HeaderRow>
            {isConditionsWatcherEnabled ? (
                <ConditionActionMenu
                    applicantId={applicantId}
                    conditionId={condition.id}
                    isWatching={condition.isWatching}
                    canDelete={condition.canDelete}
                    canEdit={condition.canEdit}
                    onDelete={onDelete}
                    onEdit={onEdit}
                    onAddFollowUp={onAddFollowUp}
                />
            ) : null}
            {condition.resolved ? (
                <FilledCheckbox aria-hidden>✓</FilledCheckbox>
            ) : (
                <Checkbox
                    role={onCheckboxClick ? 'button' : undefined}
                    aria-label={onCheckboxClick ? 'Mark task/condition resolved' : undefined}
                    aria-disabled={checkboxIsGated ? true : undefined}
                    disabled={onCheckboxClick ? resolveGate.disabled : undefined}
                    title={checkboxIsGated ? resolveGate.title : undefined}
                    data-testid={`condition-checkbox-${condition.id}`}
                    onClick={handleCheckboxClick}
                />
            )}
            <HeaderContent>
                {condition.resolved ? (
                    <StrikethroughDescription $markdown={isConditionTextFormattingEnabled}>
                        <ClampedText expanded={cardIsOpen} lines={4}>
                            {isConditionTextFormattingEnabled ? (
                                <FormattedText>{condition.description}</FormattedText>
                            ) : (
                                <Linkify>{condition.description}</Linkify>
                            )}
                        </ClampedText>
                    </StrikethroughDescription>
                ) : (
                    <Description $markdown={isConditionTextFormattingEnabled}>
                        <ClampedText expanded={cardIsOpen} lines={4}>
                            {isConditionTextFormattingEnabled ? (
                                <FormattedText>{condition.description}</FormattedText>
                            ) : (
                                <Linkify>{condition.description}</Linkify>
                            )}
                        </ClampedText>
                    </Description>
                )}
                <MetaRow>
                    <span>
                        Created by {condition.createdBy} · {formatDateShort(condition.dateCreated)}
                    </span>
                </MetaRow>
                {condition.editedBy && condition.dateEdited ? (
                    <MetaRow>
                        <span>
                            Edited by {condition.editedBy} · {formatDateTime(condition.dateEdited)}
                        </span>
                    </MetaRow>
                ) : null}
                <MetaRow>
                    {scope ? <ProductPill>{scope}</ProductPill> : null}
                    <ReviewTeamPills teams={getReviewTeams(condition.reviewTeams)} />
                    {fileCount > 0 ? (
                        <DocumentsCountPill
                            type="button"
                            onClick={handleDocumentsClick}
                            data-testid={`condition-documents-${condition.id}`}
                        >
                            📎 {fileCount} {fileCount === 1 ? 'document' : 'documents'}
                        </DocumentsCountPill>
                    ) : null}
                    {showDddPendingPill ? (
                        <DddPill
                            type={onNavigateToDdd ? 'button' : undefined}
                            variant="violet"
                            onClick={onNavigateToDdd ? handleNavigateToDddClick : undefined}
                            data-testid={`condition-ddd-pending-${condition.id}`}
                        >
                            {condition.dddOutstandingCount}/{condition.dddExpectedCount} DDD pending
                        </DddPill>
                    ) : null}
                    {blocksApprovalScope ? (
                        <WarningPill
                            variant="amber"
                            data-testid={`condition-blocks-approval-${condition.id}`}
                        >
                            ⚠ Blocks approval for {blocksApprovalScope}
                        </WarningPill>
                    ) : null}
                    {isConditionResolutionStatusEnabled && condition.parentConditionId ? (
                        onNavigateToCondition ? (
                            <LinkedConditionPillButton
                                type="button"
                                variant="orange"
                                onClick={handleNavigateClick(condition.parentConditionId)}
                                title="Jump to the task/condition this follows up on"
                                data-testid={`condition-follow-up-${condition.id}`}
                            >
                                ↳ Follow-up
                            </LinkedConditionPillButton>
                        ) : (
                            <LinkedConditionPill
                                variant="orange"
                                data-testid={`condition-follow-up-${condition.id}`}
                            >
                                ↳ Follow-up
                            </LinkedConditionPill>
                        )
                    ) : null}
                    {isConditionResolutionStatusEnabled && childCount > 0 ? (
                        onNavigateToCondition && firstChildId ? (
                            <LinkedConditionPillButton
                                type="button"
                                variant="blue"
                                onClick={handleNavigateClick(firstChildId)}
                                title={
                                    childCount === 1
                                        ? 'Jump to the follow-up task/condition'
                                        : 'Jump to the first follow-up task/condition'
                                }
                                data-testid={`condition-linked-count-${condition.id}`}
                            >
                                🔗 {childCount} follow-up{childCount === 1 ? '' : 's'}
                            </LinkedConditionPillButton>
                        ) : (
                            <LinkedConditionPill
                                variant="blue"
                                data-testid={`condition-linked-count-${condition.id}`}
                            >
                                🔗 {childCount} follow-up{childCount === 1 ? '' : 's'}
                            </LinkedConditionPill>
                        )
                    ) : null}
                </MetaRow>
                {condition.isDeleted && condition.modifiedBy && condition.dateDeleted ? (
                    <DeletedPill variant="red">
                        🗑 Deleted by {condition.modifiedBy} ·{' '}
                        {formatDateTime(condition.dateDeleted)}
                    </DeletedPill>
                ) : condition.resolved && condition.resolvedBy && condition.dateResolved ? (
                    (() => {
                        const resNote = condition.notes.find((n) => n.isResolutionNote);
                        const notePreview = resNote ? (
                            <NotesPreview style={{ marginLeft: 6 }}>
                                : &ldquo;{resNote.note}&rdquo;
                            </NotesPreview>
                        ) : null;
                        const resolvedByLine = (
                            <>
                                {condition.resolvedBy} · {formatDateTime(condition.dateResolved)}
                                {notePreview}
                            </>
                        );

                        if (isProblematic) {
                            const body = <>⚠ Problematic — resolved by {resolvedByLine}</>;
                            return onNavigateToCondition && firstChildId ? (
                                <ProblematicPillButton
                                    type="button"
                                    variant="orange"
                                    onClick={handleNavigateClick(firstChildId)}
                                    title="Jump to the follow-up task/condition"
                                    data-testid={`condition-problematic-${condition.id}`}
                                >
                                    {body}
                                </ProblematicPillButton>
                            ) : (
                                <ProblematicPill
                                    variant="orange"
                                    data-testid={`condition-problematic-${condition.id}`}
                                >
                                    {body}
                                </ProblematicPill>
                            );
                        }

                        return (
                            <ResolvedPill
                                variant="green"
                                data-testid={`condition-resolved-${condition.id}`}
                            >
                                {isApproved ? '✓ Approved — resolved by ' : '✓ Resolved by '}
                                {resolvedByLine}
                            </ResolvedPill>
                        );
                    })()
                ) : noteCount > 0 ? (
                    <NotesHint onClick={onOpen}>
                        💬 {noteCount} {noteCount === 1 ? 'note' : 'notes'}
                        {condition.notes.length > 0 ? (
                            <NotesPreview>
                                · Latest by {condition.notes[condition.notes.length - 1].createdBy}{' '}
                                on{' '}
                                {formatDateShort(
                                    condition.notes[condition.notes.length - 1].dateCreated,
                                )}
                                : &ldquo;{condition.notes[condition.notes.length - 1].note}&rdquo;
                            </NotesPreview>
                        ) : null}
                    </NotesHint>
                ) : (
                    <NoNotesText>No notes yet</NoNotesText>
                )}
            </HeaderContent>
            {isConditionsWatcherEnabled ? (
                <WatcherAvatarStack
                    applicantId={applicantId}
                    conditionId={condition.id}
                    watchers={condition.watchers ?? []}
                    isWatching={condition.isWatching}
                />
            ) : null}
        </HeaderRow>
    );
};

export const ConditionContent = ({
    applicantId,
    condition,
    onDelete,
    onResolve,
    onAttachDocument,
    onAddFollowUp,
    highlightNoteId,
}: ContentProps) => {
    const deleteGate = usePermissionGate(
        Feature.ProtectedConditions,
        PermissionDeniedMessages.DeleteCondition,
        condition.canDelete,
    );
    const resolveGate = usePermissionGate(
        Feature.ProtectedConditions,
        PermissionDeniedMessages.ResolveCondition,
        condition.canResolve,
    );

    const { isConditionAutoResolveEnabled, isConditionsWatcherEnabled } = useFeatureFlags();
    const isOpen = !condition.resolved && !condition.isDeleted;
    // Delete and Add Follow-Up move into the header's gear menu when the watcher feature is on.
    const showFooterDelete = Boolean(onDelete) && !isConditionsWatcherEnabled;
    const showFooterFollowUp = Boolean(onAddFollowUp) && !isConditionsWatcherEnabled;

    const handleAutoResolve =
        isConditionAutoResolveEnabled && isOpen && onResolve
            ? resolveGate.gateAction(() =>
                  onResolve({
                      initialNote: AUTO_RESOLVE_NOTE_TEMPLATE,
                      resolutionMethod: ConditionResolutionMethod.AcceptedAutoResolution,
                  }),
              )
            : undefined;

    return (
        <>
            <ConditionNotesTimeline
                notes={condition.notes}
                files={condition.files}
                onAutoResolve={handleAutoResolve}
                highlightNoteId={highlightNoteId}
            />
            {isOpen ? (
                <ConditionNoteForm
                    applicantId={applicantId}
                    conditionId={condition.id}
                    canResolve={condition.canResolve}
                    onResolve={onResolve}
                    onAttachDocument={onAttachDocument}
                />
            ) : null}
            {showFooterDelete || showFooterFollowUp ? (
                <ContentFooter>
                    {showFooterFollowUp && onAddFollowUp ? (
                        <Button
                            label="Add Follow-Up Task/Condition"
                            icon="pi pi-plus"
                            text
                            onClick={onAddFollowUp}
                            aria-label="Add follow-up task/condition"
                            data-testid={`condition-add-follow-up-${condition.id}`}
                        />
                    ) : null}
                    {showFooterDelete && onDelete ? (
                        <Button
                            label="Delete Task/Condition"
                            icon="pi pi-trash"
                            severity="danger"
                            text
                            onClick={deleteGate.gateAction(onDelete)}
                            aria-label="Delete task/condition"
                            aria-disabled={deleteGate.disabled || undefined}
                            data-testid={`condition-delete-${condition.id}`}
                            title={deleteGate.title}
                            style={
                                deleteGate.disabled
                                    ? { opacity: 0.6, cursor: 'not-allowed' }
                                    : undefined
                            }
                        />
                    ) : null}
                </ContentFooter>
            ) : null}
        </>
    );
};
